import { useCallback, useEffect, useRef, useState } from "react";
import type { LearningFacts, ModelEndpointSummary } from "@vocab1600/shared";
import { useSearchParams } from "react-router-dom";
import { api, streamAnalyticsReport } from "./api.js";

/** 极简 Markdown 渲染：只处理标题、列表、粗体与段落，避免为此引入额外依赖 */
function renderMarkdown(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let listBuffer: string[] = [];
  const flushList = (key: string) => {
    if (listBuffer.length === 0) return;
    nodes.push(<ul key={`ul-${key}`}>{listBuffer.map((item, i) => <li key={i}>{inline(item)}</li>)}</ul>);
    listBuffer = [];
  };
  const inline = (line: string): React.ReactNode => {
    const parts = line.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) return <b key={i}>{part.slice(2, -2)}</b>;
      if (part.startsWith("`") && part.endsWith("`")) return <code key={i}>{part.slice(1, -1)}</code>;
      return <span key={i}>{part}</span>;
    });
  };
  text.split("\n").forEach((line, index) => {
    const trimmed = line.trim();
    if (/^#{1,6}\s/.test(trimmed)) {
      flushList(String(index));
      const level = trimmed.match(/^#+/)![0].length;
      const content = trimmed.replace(/^#+\s*/, "");
      nodes.push(level <= 2
        ? <h3 key={index} className="md-h2">{content}</h3>
        : <h4 key={index} className="md-h3">{content}</h4>);
    } else if (/^[-*]\s/.test(trimmed)) {
      listBuffer.push(trimmed.replace(/^[-*]\s*/, ""));
    } else if (/^\d+[.)]\s/.test(trimmed)) {
      listBuffer.push(trimmed.replace(/^\d+[.)]\s*/, ""));
    } else if (trimmed === "") {
      flushList(String(index));
    } else {
      flushList(String(index));
      nodes.push(<p key={index}>{inline(trimmed)}</p>);
    }
  });
  flushList("end");
  return nodes;
}

export function AnalyticsPage() {
  const [facts, setFacts] = useState<LearningFacts | null>(null);
  const [models, setModels] = useState<ModelEndpointSummary[]>([]);
  const [students, setStudents] = useState<Array<{ id: string; displayName: string; email: string }>>([]);
  const [searchParams] = useSearchParams();
  const [studentId, setStudentId] = useState(searchParams.get("userId") ?? "");
  const [modelId, setModelId] = useState("");
  const [days, setDays] = useState(14);
  const [report, setReport] = useState("");
  const [status, setStatus] = useState<{ stage: string; message: string } | null>(null);
  const [error, setError] = useState("");
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const outputRef = useRef<HTMLDivElement | null>(null);

  const loadFacts = useCallback(async (window: number, user: string) => {
    try { setFacts(await api.facts(window, user || undefined)); }
    catch { /* 事实包加载失败不阻断页面，报告流程里还会再取一次 */ }
  }, []);

  useEffect(() => { void loadFacts(days, studentId); }, [days, studentId, loadFacts]);
  useEffect(() => { api.students().then(({ students }) => setStudents(students)).catch(() => setStudents([])); }, []);
  useEffect(() => {
    api.models().then(({ models }) => {
      setModels(models);
      setModelId(models.find((m) => m.isDefault && m.enabled)?.id ?? models.find((m) => m.enabled)?.id ?? "");
    }).catch(() => setModels([]));
  }, []);

  // 流式输出时自动滚动到底部，除非用户手动向上翻阅
  useEffect(() => {
    const el = outputRef.current;
    if (!el || !running) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, [report, running]);

  async function generate() {
    setRunning(true); setReport(""); setError(""); setElapsed(null);
    setStatus({ stage: "start", message: "正在建立连接…" });
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await streamAnalyticsReport({
        days,
        ...(modelId ? { modelId } : {}),
        ...(studentId ? { scope: "student", targetId: studentId } : {})
      }, (event) => {
        if (event.type === "status") setStatus({ stage: event.stage, message: event.message });
        else if (event.type === "facts") setFacts(event.facts);
        else if (event.type === "delta") { setReport((prev) => prev + event.text); setStatus(null); }
        else if (event.type === "done") { setElapsed(event.elapsedMs); setStatus(null); }
        else if (event.type === "error") setError(event.message);
      }, controller.signal);
    } catch (cause) {
      if ((cause as Error).name !== "AbortError") {
        setError(cause instanceof Error ? cause.message : "生成失败");
      }
    } finally {
      setRunning(false); setStatus(null); abortRef.current = null;
    }
  }

  const hasModel = models.some((m) => m.enabled);
  const t = facts?.totals;

  return <div className="page">
    <header className="page-header">
      <span className="eyebrow dark">ANALYTICS</span>
      <h1>AI 学情分析</h1>
      <p>
        指标全部来自真实播放事件表。可切换到某个学生查看其个人学情，报告由你在「模型管理」中配置的
        大模型生成，流式返回，过程可见。
      </p>
    </header>

    <section className="metric-grid">
      <div className="metric-card"><small>活跃学习者</small><strong>{t?.activeLearners ?? "—"}</strong><span>共 {t?.totalLearners ?? "—"} 名账号</span></div>
      <div className="metric-card"><small>累计收听</small><strong>{t?.listeningMinutes ?? "—"}</strong><span>分钟 · 最近 {days} 天</span></div>
      <div className="metric-card"><small>播放次数</small><strong>{t?.playStarts ?? "—"}</strong><span>完成 {t?.playCompletions ?? "—"} 次</span></div>
      <div className="metric-card"><small>完成率</small><strong>{t ? `${Math.round(t.completionRate * 100)}%` : "—"}</strong><span>覆盖 {t?.distinctSongs ?? "—"} 首歌</span></div>
    </section>

    {facts && (
      <p className="muted small">当前范围：{facts.scopeLabel}　统计窗口：最近 {facts.windowDays} 天</p>
    )}

    {students.length === 0 && (
      <div className="form-notice block">
        还没有任何学生账号，因此无法按学生查看学情。请先到「用户管理」创建学生账号
        （角色勾选“学生”），学生登录并收听后，这里就能选择具体某个人查看其指标与 AI 报告。
      </div>
    )}

    {facts && !facts.dataQuality.hasEvents && (
      <div className="form-notice block">
        最近 {days} 天内{studentId ? "该学生" : ""}没有任何真实学习事件。可能是学生账号尚未创建、还没有人开始收听，或事件采集刚刚上线。
        系统不会为此编造数据。
      </div>
    )}

    <section className="panel">
      <div className="panel-title">
        <h3>生成学情报告</h3>
        <div className="toolbar tight">
          <select value={studentId} onChange={(e) => setStudentId(e.target.value)} disabled={running || students.length === 0}>
            <option value="">{students.length === 0 ? "尚无学生账号" : "全机构"}</option>
            {students.map((student) => (
              <option key={student.id} value={student.id}>{student.displayName}</option>
            ))}
          </select>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} disabled={running}>
            <option value={7}>最近 7 天</option>
            <option value={14}>最近 14 天</option>
            <option value={30}>最近 30 天</option>
            <option value={90}>最近 90 天</option>
          </select>
          <select value={modelId} onChange={(e) => setModelId(e.target.value)} disabled={running || !hasModel}>
            {models.filter((m) => m.enabled).map((model) => (
              <option key={model.id} value={model.id}>{model.displayName}{model.isDefault ? "（默认）" : ""}</option>
            ))}
            {!hasModel && <option value="">尚未配置可用模型</option>}
          </select>
          {running ? (
            <button className="ghost-button" onClick={() => abortRef.current?.abort()}>停止</button>
          ) : (
            <button className="primary-button compact" onClick={() => void generate()} disabled={!hasModel}>
              生成报告
            </button>
          )}
        </div>
      </div>

      {!hasModel && (
        <div className="form-error block">
          尚未配置可用的大模型。请先到「模型管理」新增一个模型并测试连通，再回到这里生成报告。
        </div>
      )}
      {error && <div className="form-error block">{error}</div>}

      {status && (
        <div className="stream-status">
          <span className="spinner" aria-hidden="true" />
          <span>{status.message}</span>
        </div>
      )}

      {(report || running) && (
        <div className="report-output" ref={outputRef}>
          {renderMarkdown(report)}
          {running && <span className="caret" aria-hidden="true" />}
        </div>
      )}

      {elapsed !== null && !running && (
        <p className="muted small">报告生成完成，用时 {(elapsed / 1000).toFixed(1)} 秒。</p>
      )}
    </section>

    {facts && facts.topSongs.length > 0 && (
      <section className="panel">
        <h3>播放最多的歌曲</h3>
        <div className="data-table-wrap">
          <table className="data-table">
            <thead><tr><th>编号</th><th>歌名</th><th className="right">播放</th><th className="right">完成</th><th className="right">分钟</th></tr></thead>
            <tbody>
              {facts.topSongs.map((song) => (
                <tr key={song.songNumber}>
                  <td className="mono">{song.songNumber}</td><td>{song.title}</td>
                  <td className="right">{song.plays}</td><td className="right">{song.completions}</td><td className="right">{song.minutes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    )}

    {facts && facts.variantPreference.length > 0 && (
      <section className="panel">
        <h3>旋律版本偏好</h3>
        <p className="muted small">同一首歌的不同旋律版本被选择的次数，可用于判断哪个版本更受欢迎。</p>
        <div className="variant-bars">
          {facts.variantPreference.map((item) => {
            const max = Math.max(...facts.variantPreference.map((v) => v.plays), 1);
            return <div key={item.variantKey} className="variant-bar">
              <span className="mono">{item.variantKey}</span>
              <i style={{ width: `${(item.plays / max) * 100}%` }} />
              <b>{item.plays}</b>
            </div>;
          })}
        </div>
      </section>
    )}
  </div>;
}
